import { useEffect, useRef, useState } from 'react';
import { connect, LocalVideoTrack } from 'twilio-video';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, ScreenShare, ScreenShareOff, Volume2, VolumeX, Headphones } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CallModal — full-screen 1:1 video/voice call using Twilio Video.
 *
 * Props:
 *   token         string   Twilio AccessToken (from POST /api/calls/token)
 *   roomName      string   Deterministic room ("match_<match_id>")
 *   mode          'video' | 'voice'    Voice mode disables camera track pub.
 *   otherName     string   Callee display name for header.
 *   otherPhoto    string?  Avatar to show while waiting for their video.
 *   onEnd         () => void  Called when the room disconnects or user hangs up.
 *
 * Behaviour:
 *   - Joins the given Twilio room, publishes local audio (+ camera if video).
 *   - Renders remote participant's tracks as they arrive.
 *   - Mute / camera / screen-share / hangup controls in the bottom bar.
 *   - Screen share (video mode only): replaces the camera track with a
 *     screen-capture track via `getDisplayMedia`; auto-reverts to camera
 *     when the user hits the browser's "Stop sharing" button.
 *   - Auto-hangup if remote participant disconnects after being connected.
 */
export default function CallModal({ token, roomName, mode = 'video', otherName, otherPhoto, onEnd }) {
  const [status, setStatus] = useState('connecting'); // connecting | waiting | live | ended
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(mode === 'video');
  const [screenOn, setScreenOn] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Sep 6 2026 · Output-audio mute. Independent of the mic toggle — this
  // hides the OTHER person's audio from us (useful for stepping away
  // briefly without ending the call). Toggles the muted attribute on
  // every remote <audio>/<video> element inline; doesn't touch Twilio
  // room state so the peer never learns we can't hear them.
  const [outputMuted, setOutputMuted] = useState(false);
  // Chrome/Edge-only audio output device picker. Enumerates via
  // navigator.mediaDevices.enumerateDevices() and routes remote media
  // via HTMLMediaElement.setSinkId(). Silently unavailable on Safari
  // and Firefox — we hide the button on those.
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [activeSinkId, setActiveSinkId] = useState('default');
  const [showOutputPicker, setShowOutputPicker] = useState(false);
  const roomRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteMediaRef = useRef(null);
  // Cache the original camera track so we can restore it when the user
  // stops screen-sharing without re-requesting camera permission.
  const cameraTrackRef = useRef(null);
  const screenTrackRef = useRef(null);
  const startedAtRef = useRef(null);

  // Detect setSinkId support once (Chromium-based only).
  const supportsOutputPicker = typeof HTMLMediaElement !== 'undefined'
    && typeof HTMLMediaElement.prototype.setSinkId === 'function';

  // Enumerate audio output devices when the picker opens.
  useEffect(() => {
    if (!showOutputPicker || !supportsOutputPicker) return;
    (async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setAudioOutputs(devices.filter(d => d.kind === 'audiooutput'));
      } catch (e) {
        console.warn('enumerateDevices failed:', e);
      }
    })();
  }, [showOutputPicker, supportsOutputPicker]);

  // Timer while live
  useEffect(() => {
    if (status !== 'live') return;
    startedAtRef.current = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const room = await connect(token, {
          name: roomName,
          audio: true,
          video: mode === 'video' ? { width: 640, height: 480 } : false,
        });
        if (cancelled) { room.disconnect(); return; }
        roomRef.current = room;

        // Attach local video preview + cache the camera track ref so we can
        // restore it after screen-sharing stops.
        room.localParticipant.videoTracks.forEach((pub) => {
          if (pub.track) {
            cameraTrackRef.current = pub.track;
            if (localVideoRef.current) localVideoRef.current.appendChild(pub.track.attach());
          }
        });

        setStatus(room.participants.size > 0 ? 'live' : 'waiting');

        const attachRemote = (participant) => {
          setStatus('live');
          const attach = (track) => {
            if (track && remoteMediaRef.current) {
              const el = track.attach();
              // Sep 6 2026 — apply current output-mute + sink-id to
              // newly-arrived remote tracks so they honor the user's
              // active preferences from the moment they attach.
              try { el.muted = outputMuted; } catch { /* noop */ }
              if (supportsOutputPicker && activeSinkId && activeSinkId !== 'default') {
                try { el.setSinkId?.(activeSinkId); } catch { /* noop */ }
              }
              remoteMediaRef.current.appendChild(el);
            }
          };
          participant.tracks.forEach((pub) => { if (pub.track) attach(pub.track); });
          participant.on('trackSubscribed', attach);
          participant.on('trackUnsubscribed', (track) => {
            try { track.detach().forEach((el) => el.remove()); } catch { /* noop */ }
          });
        };

        room.participants.forEach(attachRemote);
        room.on('participantConnected', attachRemote);
        room.on('participantDisconnected', () => {
          // Other side left — end call for us too.
          endCall();
        });
        room.on('disconnected', () => setStatus('ended'));
      } catch (e) {
        console.error('Twilio connect failed:', e);
        toast.error('Could not connect the call');
        onEnd?.();
      }
    })();
    return () => {
      cancelled = true;
      if (roomRef.current) {
        try { roomRef.current.disconnect(); } catch { /* noop */ }
        roomRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, roomName]);

  const toggleMic = () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !micOn;
    room.localParticipant.audioTracks.forEach((pub) => {
      if (pub.track) next ? pub.track.enable() : pub.track.disable();
    });
    setMicOn(next);
  };

  // Sep 6 2026 · Output audio mute toggle.
  // Toggles the `muted` attribute on every currently-attached remote
  // <audio> or <video> element inside remoteMediaRef. Twilio's `.attach()`
  // returns raw MediaElement DOM nodes appended into remoteMediaRef, so
  // we can just walk children.
  const toggleOutput = () => {
    const next = !outputMuted;
    setOutputMuted(next);
    const container = remoteMediaRef.current;
    if (!container) return;
    container.querySelectorAll('audio, video').forEach((el) => {
      try { el.muted = next; } catch { /* noop */ }
    });
  };

  // Sep 6 2026 · Audio output device picker (Chrome/Edge only).
  // Routes remote media through the selected output device via setSinkId.
  const pickOutputDevice = async (deviceId) => {
    if (!supportsOutputPicker) return;
    const container = remoteMediaRef.current;
    if (!container) return;
    try {
      const els = Array.from(container.querySelectorAll('audio, video'));
      await Promise.all(els.map((el) => el.setSinkId(deviceId)));
      setActiveSinkId(deviceId);
      setShowOutputPicker(false);
    } catch (e) {
      console.warn('setSinkId failed:', e);
      toast.error("Couldn't switch audio output");
    }
  };

  const toggleCam = () => {
    const room = roomRef.current;
    if (!room || mode !== 'video') return;
    if (screenOn) return;   // ignore cam toggle while screen-sharing
    const next = !camOn;
    room.localParticipant.videoTracks.forEach((pub) => {
      if (pub.track) next ? pub.track.enable() : pub.track.disable();
    });
    setCamOn(next);
  };

  /**
   * Screen sharing — video mode only. Publishes a screen-capture track to
   * the room and replaces the camera preview locally. When the user hits
   * the browser's "Stop sharing" button, the MediaStreamTrack fires
   * `onended` which triggers automatic revert to the camera.
   *
   * Not supported on iOS Safari — `getDisplayMedia` is undefined there.
   * We toast a friendly message and no-op.
   */
  const startScreenShare = async () => {
    const room = roomRef.current;
    if (!room || mode !== 'video' || screenOn) return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error('Screen sharing isn\u2019t available in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15, width: 1920, height: 1080 },
        audio: false,
      });
      const mediaTrack = stream.getVideoTracks()[0];
      if (!mediaTrack) throw new Error('No screen track');
      const screenTrack = new LocalVideoTrack(mediaTrack, { name: 'screen' });
      screenTrackRef.current = screenTrack;

      // Swap: unpublish camera, publish screen. Keeping the camera track
      // instance around (cached in cameraTrackRef) so restore is instant.
      if (cameraTrackRef.current) {
        await room.localParticipant.unpublishTrack(cameraTrackRef.current);
      }
      await room.localParticipant.publishTrack(screenTrack);

      // Update local preview to show what's being shared.
      if (localVideoRef.current) {
        localVideoRef.current.innerHTML = '';
        localVideoRef.current.appendChild(screenTrack.attach());
      }

      // Browser toolbar "Stop sharing" click → auto-revert.
      mediaTrack.addEventListener('ended', () => { stopScreenShare(); });

      setScreenOn(true);
      toast.success('Screen sharing started');
    } catch (e) {
      // User cancelled the picker or denied permission — silent.
      if (e?.name === 'NotAllowedError' || e?.name === 'AbortError') return;
      console.error('screen-share failed:', e);
      toast.error('Could not start screen sharing');
    }
  };

  const stopScreenShare = async () => {
    const room = roomRef.current;
    if (!room || !screenOn) return;
    try {
      const screenTrack = screenTrackRef.current;
      if (screenTrack) {
        try { room.localParticipant.unpublishTrack(screenTrack); } catch { /* noop */ }
        try { screenTrack.stop(); } catch { /* noop */ }
        screenTrackRef.current = null;
      }
      // Restore camera publish (if we still have the track).
      if (cameraTrackRef.current && mode === 'video') {
        await room.localParticipant.publishTrack(cameraTrackRef.current);
        if (localVideoRef.current) {
          localVideoRef.current.innerHTML = '';
          localVideoRef.current.appendChild(cameraTrackRef.current.attach());
        }
        // If cam was off when share started, keep it off.
        if (!camOn) cameraTrackRef.current.disable();
      }
    } catch (e) {
      console.warn('screen-share stop cleanup:', e);
    } finally {
      setScreenOn(false);
    }
  };

  const toggleScreen = () => (screenOn ? stopScreenShare() : startScreenShare());

  const endCall = () => {
    if (roomRef.current) {
      try { roomRef.current.disconnect(); } catch { /* noop */ }
      roomRef.current = null;
    }
    setStatus('ended');
    onEnd?.();
  };

  const fmtElapsed = () => {
    const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
    const s = (elapsed % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0F0F10] flex flex-col" data-testid="call-modal">
      {/* Header */}
      <div className="px-5 pt-6 pb-3 flex items-center justify-between text-white">
        <div className="flex items-center gap-3 min-w-0">
          {otherPhoto && (
            <img src={otherPhoto} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-[15px] font-semibold truncate">{otherName || 'Call'}</p>
            <p className="text-[11px] text-white/60">
              {status === 'connecting' && 'Connecting…'}
              {status === 'waiting' && 'Ringing…'}
              {status === 'live' && `${mode === 'video' ? 'Video call' : 'Voice call'} · ${fmtElapsed()}`}
              {status === 'ended' && 'Call ended'}
            </p>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.14em] text-white/50 font-semibold">
          {screenOn ? (
            <span data-testid="call-sharing-indicator" className="inline-flex items-center gap-1 text-emerald-300 tracking-wide">
              <ScreenShare size={10} strokeWidth={2.4}/> Sharing screen
            </span>
          ) : 'Meet to Keep'}
        </span>
      </div>

      {/* Remote media (fills) */}
      <div className="flex-1 relative bg-[#0A0A0B] overflow-hidden">
        <div
          ref={remoteMediaRef}
          data-testid="call-remote-video"
          className="absolute inset-0 [&_video]:w-full [&_video]:h-full [&_video]:object-cover flex items-center justify-center"
        />
        {(status === 'connecting' || status === 'waiting') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            {otherPhoto ? (
              <img src={otherPhoto} alt="" className="w-24 h-24 rounded-full object-cover ring-4 ring-white/10" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/10" />
            )}
            <Loader2 className="w-6 h-6 animate-spin text-white/60" />
            <p className="text-white/70 text-sm font-medium">
              {status === 'connecting' ? 'Connecting to call…' : `Ringing ${otherName || ''}…`}
            </p>
          </div>
        )}
        {/* Local self-view */}
        {mode === 'video' && (
          <div className="absolute bottom-24 right-4 w-32 h-44 rounded-2xl overflow-hidden bg-black/50 ring-1 ring-white/10">
            <div
              ref={localVideoRef}
              data-testid="call-local-video"
              className="w-full h-full [&_video]:w-full [&_video]:h-full [&_video]:object-cover"
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="px-6 pb-8 pt-4 flex items-center justify-center gap-4 relative">
        <button
          onClick={toggleMic}
          data-testid="call-mic-toggle"
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
            micOn ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-white text-[#0F0F10]'
          }`}
          aria-label={micOn ? 'Mute' : 'Unmute'}
        >
          {micOn ? <Mic size={20} strokeWidth={2}/> : <MicOff size={20} strokeWidth={2}/>}
        </button>

        {/* Sep 6 2026 — Output audio mute (mute the other person). */}
        <button
          onClick={toggleOutput}
          data-testid="call-output-toggle"
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
            outputMuted ? 'bg-white text-[#0F0F10]' : 'bg-white/15 text-white hover:bg-white/25'
          }`}
          aria-label={outputMuted ? 'Unmute their audio' : 'Mute their audio'}
        >
          {outputMuted ? <VolumeX size={20} strokeWidth={2}/> : <Volume2 size={20} strokeWidth={2}/>}
        </button>

        {/* Sep 6 2026 — Audio output device picker. Chromium-only. */}
        {supportsOutputPicker && (
          <div className="relative">
            <button
              onClick={() => setShowOutputPicker((v) => !v)}
              data-testid="call-output-picker-toggle"
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                showOutputPicker ? 'bg-white text-[#0F0F10]' : 'bg-white/15 text-white hover:bg-white/25'
              }`}
              aria-label="Choose audio output"
            >
              <Headphones size={20} strokeWidth={2}/>
            </button>
            {showOutputPicker && (
              <div
                className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-64 max-h-64 overflow-y-auto rounded-2xl bg-[#1a1a1a] border border-white/10 shadow-2xl p-2 z-10"
                data-testid="call-output-picker-menu"
              >
                <p className="text-[10px] tracking-[0.14em] uppercase font-bold text-white/50 px-3 py-2">
                  Audio output
                </p>
                {audioOutputs.length === 0 && (
                  <p className="text-[12px] text-white/60 px-3 py-2">Detecting devices…</p>
                )}
                {audioOutputs.map((d) => (
                  <button
                    key={d.deviceId}
                    onClick={() => pickOutputDevice(d.deviceId)}
                    data-testid={`call-output-device-${d.deviceId}`}
                    className={`w-full text-left rounded-xl px-3 py-2 text-[13px] transition ${
                      activeSinkId === d.deviceId
                        ? 'bg-white text-[#0F0F10] font-semibold'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    {d.label || `Device ${d.deviceId.slice(0, 6)}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {mode === 'video' && (
          <button
            onClick={toggleCam}
            data-testid="call-cam-toggle"
            disabled={screenOn}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              camOn && !screenOn ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-white text-[#0F0F10]'
            } ${screenOn ? 'opacity-40 cursor-not-allowed' : ''}`}
            aria-label={camOn ? 'Camera off' : 'Camera on'}
          >
            {camOn ? <Video size={20} strokeWidth={2}/> : <VideoOff size={20} strokeWidth={2}/>}
          </button>
        )}
        {mode === 'video' && (
          <button
            onClick={toggleScreen}
            data-testid="call-screen-share-toggle"
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              screenOn ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-white/15 text-white hover:bg-white/25'
            }`}
            aria-label={screenOn ? 'Stop sharing' : 'Share screen'}
          >
            {screenOn ? <ScreenShareOff size={20} strokeWidth={2}/> : <ScreenShare size={20} strokeWidth={2}/>}
          </button>
        )}
        <button
          onClick={endCall}
          data-testid="call-hangup"
          className="w-16 h-16 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white flex items-center justify-center transition-all active:scale-95"
          aria-label="End call"
        >
          <PhoneOff size={22} strokeWidth={2.2}/>
        </button>
      </div>
    </div>
  );
}
